"use client";

import Link from "next/link";
import { BrandMark } from "@/client/components/brand/BrandMark";
import { useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { shellPrefetch } from "./shellPrefetch";

export interface PublicNavLink {
  href: string;
  label: string;
}

export const DEFAULT_PUBLIC_NAV_LINKS: PublicNavLink[] = [
  { href: "/", label: "Home" },
  { href: "/pricing", label: "Pricing" },
  { href: "/for-funeral-homes", label: "For funeral homes" },
];

export interface PublicHeaderProps {
  /**
   * Nav links rendered between the wordmark and the Sign In button. Pass an
   * empty array for surfaces that should show the wordmark only.
   */
  navLinks?: PublicNavLink[];
  /** Accessible name for the nav landmark. */
  navLabel?: string;
  /**
   * Href of the current page. When it matches a nav link, that link renders in
   * the active (Espresso) treatment.
   */
  activeHref?: string;
  /**
   * Whether to render the Sign In call to action.
   * @default true
   */
  showSignIn?: boolean;
}

/**
 * PublicHeader — the one header for every unauthenticated surface.
 *
 * Consolidates the four near-duplicate headers the audit found (marketing
 * shell, legal pages, support page, landing page) into a single sticky bar so
 * the wordmark, nav underline treatment, and mobile menu behave identically
 * everywhere. Nav link text is contract-tested; change layout, not labels.
 */
export function PublicHeader({
  navLinks = DEFAULT_PUBLIC_NAV_LINKS,
  navLabel = "Marketing navigation",
  activeHref,
  showSignIn = true,
}: PublicHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hasNav = navLinks.length > 0;
  const hasMobileMenu = hasNav || showSignIn;

  return (
    <header className="sticky top-0 z-30 border-b border-warm-200 bg-warm-50/85 backdrop-blur">
      <div className="mx-auto flex max-w-[1300px] items-center justify-between gap-4 px-6 py-4 md:px-12">
        <Link
          href="/"
          className="flex shrink-0 items-baseline gap-2.5 outline-none focus-visible:ring-2 focus-visible:ring-espresso-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <BrandMark size={28} className="self-center" />
          <span className="font-serif text-[28px] text-warm-950">
            Memoriam
          </span>
          <span className="text-[11px] uppercase tracking-[0.2em] text-warm-700">tributes</span>
        </Link>

        {hasMobileMenu && (
          <nav
            aria-label={navLabel}
            className="hidden items-center justify-end gap-6 text-sm xl:flex"
          >
            {navLinks.map((link) => {
              const isActive = activeHref === link.href;

              return (
                <Link
                  key={link.href}
                  href={link.href}
                  prefetch={shellPrefetch(link.href)}
                  aria-current={isActive ? "page" : undefined}
                  className={`group relative py-1 font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-espresso-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                    isActive
                      ? "text-espresso-700"
                      : "text-warm-700 hover:text-warm-950"
                  }`}
                >
                  {link.label}
                  <span
                    aria-hidden="true"
                    className={`absolute inset-x-0 -bottom-0.5 h-px origin-left bg-espresso-600 transition-transform duration-200 ${
                      isActive
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </Link>
              );
            })}
            {showSignIn && (
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="lg"><Link href="/dashboard" prefetch={false}>Sign in</Link></Button>
                <Button asChild size="lg"><Link href="/dashboard" prefetch={false}>Begin a memorial</Link></Button>
              </div>
            )}
          </nav>
        )}

        {hasMobileMenu && (
          <Button
            type="button"
            variant="ghost"
            size="icon-lg"
            className="xl:hidden"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
            aria-controls="public-mobile-navigation"
          >
            <Menu size={24} className="text-warm-950" />
          </Button>
        )}
      </div>

      {hasMobileMenu && mobileMenuOpen && (
        <nav id="public-mobile-navigation" aria-label={`${navLabel} mobile`} className="border-t border-warm-200 bg-background xl:hidden">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              prefetch={shellPrefetch(link.href)}
              aria-current={activeHref === link.href ? "page" : undefined}
              className="block border-b border-warm-200 px-6 py-4 text-sm font-medium text-warm-950 outline-none hover:bg-warm-100 focus-visible:bg-warm-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          {showSignIn && (
            <div className="grid gap-3 p-6 sm:grid-cols-2">
              <Button asChild variant="ghost" size="xl">
                <Link href="/dashboard" prefetch={false} onClick={() => setMobileMenuOpen(false)}>Sign in</Link>
              </Button>
              <Button asChild size="xl">
                <Link href="/dashboard" prefetch={false} onClick={() => setMobileMenuOpen(false)}>Begin a memorial</Link>
              </Button>
            </div>
          )}
        </nav>
      )}
    </header>
  );
}
